import { getImageUrl } from './imageData';

const CART_STORAGE_KEY = 'etc_cart';
const CART_SCOPE_KEY = 'etc_cart_scope';
const LEGACY_CART_STORAGE_KEYS = ['etc_cart', 'cart'];
const DEFAULT_CART_SCOPE = 'guest';

function normalizeCartKind(value, fallback = 'product') {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === 'spare-part' || raw === 'spare_part' || raw === 'sparepart' || raw === 'spare parts') {
    return 'spare-part';
  }
  if (raw === 'product') return 'product';
  return fallback;
}

function normalizeCartItem(item, fallbackKind = 'product') {
  if (!item || typeof item !== 'object') return null;
  const id = item?._id || item?.id || item?.slug || item?.name;
  const inferredKind =
    item?.kind ||
    item?.itemKind ||
    item?.sourceType ||
    (item?.category && !item?.productType ? 'spare-part' : '');
  const kind = normalizeCartKind(inferredKind, fallbackKind);
  const safeId = String(id || '').trim();
  if (!safeId) return null;

  return {
    ...item,
    id: safeId,
    kind,
    cartKey: String(item?.cartKey || `${kind}:${safeId}`),
    name: item?.name || item?.title || 'Machine',
    price: Number(item?.price ?? item?.unitPrice ?? item?.amount ?? 0),
    image: Array.isArray(item?.images)
      ? getImageUrl(item.images[0])
      : getImageUrl(item?.image || ''),
    productType: item?.productType || '',
    category: item?.category || '',
    quantity: Number(item?.quantity || 1) > 0 ? Number(item.quantity) : 1,
  };
}

function normalizeProduct(product, fallbackKind = 'product') {
  return normalizeCartItem(product, fallbackKind);
}

function parseCart(raw) {
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
    if (Array.isArray(parsed?.items)) return parsed.items;
    return [];
  } catch (err) {
    return [];
  }
}

function normalizeCartScope(scope) {
  const raw = String(scope || '').trim();
  return raw || DEFAULT_CART_SCOPE;
}

function getCartStorageKey(scope) {
  return `${CART_STORAGE_KEY}:${normalizeCartScope(scope)}`;
}

function readStoredCart(scope) {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(getCartStorageKey(scope));
    return parseCart(raw || '[]')
      .map((item) => normalizeCartItem(item, item?.kind || 'product'))
      .filter(Boolean);
  } catch (err) {
    return [];
  }
}

function readLegacyCart() {
  if (typeof window === 'undefined') return [];
  for (const key of LEGACY_CART_STORAGE_KEYS) {
    const items = parseCart(localStorage.getItem(key) || '[]')
      .map((item) => normalizeCartItem(item, item?.kind || 'product'))
      .filter(Boolean);
    if (items.length > 0) {
      return { key, items };
    }
  }
  return { key: '', items: [] };
}

function writeStoredCart(scope, items) {
  if (typeof window === 'undefined') return [];
  const normalized = Array.isArray(items)
    ? items.map((item) => normalizeCartItem(item, item?.kind || 'product')).filter(Boolean)
    : [];
  localStorage.setItem(getCartStorageKey(scope), JSON.stringify(normalized));
  return normalized;
}

function mergeCartLists(baseItems = [], incomingItems = []) {
  const merged = Array.isArray(baseItems)
    ? baseItems.map((item) => normalizeCartItem(item, item?.kind || 'product')).filter(Boolean)
    : [];

  const additions = Array.isArray(incomingItems)
    ? incomingItems.map((item) => normalizeCartItem(item, item?.kind || 'product')).filter(Boolean)
    : [];

  additions.forEach((incoming) => {
    const identity = resolveCartIdentity(incoming, incoming.kind);
    const index = findCartItemIndex(merged, identity);
    if (index >= 0) {
      merged[index] = {
        ...merged[index],
        quantity: Number(merged[index].quantity || 1) + Number(incoming.quantity || 1),
      };
      return;
    }

    merged.push(incoming);
  });

  return merged;
}

function dispatchCartUpdated() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event('cart-updated'));
}

export function getCartItems() {
  if (typeof window === 'undefined') return [];
  const scope = normalizeCartScope(localStorage.getItem(CART_SCOPE_KEY));
  const scopedItems = readStoredCart(scope);
  if (scopedItems.length > 0 || scope !== DEFAULT_CART_SCOPE) {
    return scopedItems;
  }

  const legacy = readLegacyCart();
  return legacy.items;
}

export function getCartScope() {
  if (typeof window === 'undefined') return DEFAULT_CART_SCOPE;
  return normalizeCartScope(localStorage.getItem(CART_SCOPE_KEY));
}

export function setCartScope(scope, options = {}) {
  if (typeof window === 'undefined') return DEFAULT_CART_SCOPE;

  const nextScope = normalizeCartScope(scope);
  const previousScope = getCartScope();
  localStorage.setItem(CART_SCOPE_KEY, nextScope);

  const shouldAdoptLegacy = Boolean(options?.adoptLegacy);
  if (shouldAdoptLegacy && nextScope !== DEFAULT_CART_SCOPE) {
    const legacy = readLegacyCart();
    if (legacy.items.length > 0) {
      const nextStoredItems = readStoredCart(nextScope);
      const merged = mergeCartLists(nextStoredItems, legacy.items);
      writeStoredCart(nextScope, merged);
      localStorage.removeItem(legacy.key);
    }
  }

  if (previousScope !== nextScope) {
    dispatchCartUpdated();
  }

  return nextScope;
}

export function adoptGuestCartToScope(scope) {
  if (typeof window === 'undefined') return [];

  const nextScope = normalizeCartScope(scope);
  if (nextScope === DEFAULT_CART_SCOPE) return readStoredCart(nextScope);

  const scopedItems = readStoredCart(nextScope);
  const guestItems = readStoredCart(DEFAULT_CART_SCOPE);
  const legacy = readLegacyCart();
  const mergedItems = mergeCartLists(scopedItems, guestItems);
  const nextItems = mergeCartLists(mergedItems, legacy.items);
  const didMerge = guestItems.length > 0 || legacy.items.length > 0;

  if (guestItems.length > 0) {
    localStorage.removeItem(getCartStorageKey(DEFAULT_CART_SCOPE));
  }

  if (legacy.items.length > 0) {
    localStorage.removeItem(legacy.key);
  }

  if (didMerge) {
    writeStoredCart(nextScope, nextItems);
  }
  setCartScope(nextScope);
  dispatchCartUpdated();
  return nextItems;
}

export function setCartItems(items) {
  if (typeof window === 'undefined') return;
  const scope = getCartScope();
  const normalized = Array.isArray(items)
    ? items.map((item) => normalizeCartItem(item, item?.kind || 'product')).filter(Boolean)
    : [];
  localStorage.setItem(getCartStorageKey(scope), JSON.stringify(normalized));
  dispatchCartUpdated();
}

function resolveCartIdentity(product, fallbackKind = 'product') {
  const normalized = normalizeCartItem(product, fallbackKind);
  if (!normalized) return null;
  return {
    id: normalized.id,
    kind: normalized.kind,
    cartKey: normalized.cartKey,
  };
}

function findCartItemIndex(items, identity) {
  if (!identity) return -1;
  return items.findIndex((item) => {
    if (identity.cartKey && String(item.cartKey || '').trim() === identity.cartKey) return true;
    if (String(item.id) !== identity.id) return false;
    if (!identity.kind) return true;
    return String(item.kind || '') === identity.kind;
  });
}

export function addToCart(product, quantity = 1, options = {}) {
  if (typeof window === 'undefined') return [];
  const safeQuantity = Number(quantity) > 0 ? Number(quantity) : 1;
  const normalized = normalizeProduct(product, options?.kind || product?.kind || 'product');
  if (!normalized?.id) return getCartItems();

  const current = getCartItems();
  const index = findCartItemIndex(current, {
    id: normalized.id,
    kind: normalized.kind,
    cartKey: normalized.cartKey,
  });

  if (index >= 0) {
    const next = [...current];
    next[index] = {
      ...next[index],
      kind: normalized.kind,
      cartKey: normalized.cartKey,
      quantity: Number(next[index].quantity || 1) + safeQuantity,
    };
    setCartItems(next);
    return next;
  }

  const next = [...current, { ...normalized, quantity: safeQuantity }];
  setCartItems(next);
  return next;
}

export function removeFromCart(productId) {
  if (typeof window === 'undefined') return [];
  const normalizedId = String(productId || '').trim();
  const next = getCartItems().filter((item) => {
    if (!normalizedId) return true;
    if (String(item.cartKey || '') === normalizedId) return false;
    return String(item.id) !== normalizedId;
  });
  setCartItems(next);
  return next;
}

export function updateCartQuantity(productId, quantity) {
  if (typeof window === 'undefined') return [];
  const safe = Number(quantity);
  if (!Number.isFinite(safe) || safe <= 0) {
    return removeFromCart(productId);
  }
  const normalizedId = String(productId || '').trim();
  const next = getCartItems().map((item) =>
    String(item.cartKey || '') === normalizedId || String(item.id) === normalizedId
      ? { ...item, quantity: safe }
      : item
  );
  setCartItems(next);
  return next;
}

export function clearCart() {
  if (typeof window === 'undefined') return [];
  const scope = getCartScope();
  localStorage.setItem(getCartStorageKey(scope), JSON.stringify([]));
  dispatchCartUpdated();
  return [];
}
